import React, { useState, useEffect, useMemo } from 'react';
import { Shield, AlertTriangle, AlertCircle, Info, ChevronRight, ExternalLink, EyeOff } from 'lucide-react';
import { Button } from './Button';
import styles from './SecurityScanPanel.module.css';

type Severity = 'error' | 'warning' | 'info';

interface SecurityIssue {
  id: string;
  severity: Severity;
  title: string;
  description: string;
  file?: string;
  line?: number;
  outdated?: boolean;
}

interface SecurityScanPanelProps {
  files: Array<{ path?: string; fileName?: string; filePath?: string; content?: string }>;
  onScanComplete?: (issues: SecurityIssue[]) => void;
}

// ── Security Rules ──────────────────────────────────────────────────────────

function scanFiles(
  files: Array<{ path?: string; fileName?: string; filePath?: string; content?: string }>,
): SecurityIssue[] {
  const issues: SecurityIssue[] = [];
  let idCounter = 0;
  const nextId = () => `issue-${++idCounter}`;

  for (const file of files) {
    const name = file.fileName || file.path || '';
    const content = file.content || '';
    const lines = content.split('\n');

    // ── Hardcoded secrets / API keys ──
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // API keys, tokens, secrets in code
      if (/(?:api[_-]?key|secret|token|password|auth)\s*[:=]\s*['"][A-Za-z0-9_\-]{16,}['"]/i.test(line) &&
          !line.trim().startsWith('//') && !line.trim().startsWith('*')) {
        issues.push({
          id: nextId(),
          severity: 'error',
          title: 'Hardcoded Secret or API Key Detected',
          description: `Potential secret or API key found in code. Use environment variables instead.`,
          file: name,
          line: i + 1,
        });
      }

      // AWS access keys
      if (/AKIA[0-9A-Z]{16}/.test(line)) {
        issues.push({
          id: nextId(),
          severity: 'error',
          title: 'AWS Access Key Exposed',
          description: 'AWS access key ID found in source code. Rotate immediately and use env vars.',
          file: name,
          line: i + 1,
        });
      }

      // Private keys
      if (/-----BEGIN (?:RSA |EC |DSA )?PRIVATE KEY-----/.test(line)) {
        issues.push({
          id: nextId(),
          severity: 'error',
          title: 'Private Key Embedded in Source',
          description: 'A private key is embedded in the published files. This is a critical security risk.',
          file: name,
          line: i + 1,
        });
      }
    }

    // ── .env files being published ──
    if (/^\.env/.test(name)) {
      issues.push({
        id: nextId(),
        severity: 'error',
        title: 'Environment File Being Published',
        description: `"${name}" contains secrets and should never be deployed publicly.`,
        file: name,
      });
    }

    // ── XSS: innerHTML / dangerouslySetInnerHTML with user input ──
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (/\.innerHTML\s*=/.test(line) && !/\.textContent/.test(line)) {
        issues.push({
          id: nextId(),
          severity: 'warning',
          title: 'Potential XSS via innerHTML',
          description: 'Using innerHTML with dynamic content can lead to cross-site scripting. Use textContent or sanitize input.',
          file: name,
          line: i + 1,
        });
      }
      if (/dangerouslySetInnerHTML/.test(line)) {
        issues.push({
          id: nextId(),
          severity: 'warning',
          title: 'dangerouslySetInnerHTML Used',
          description: 'Ensure the HTML content is sanitized before rendering to prevent XSS attacks.',
          file: name,
          line: i + 1,
        });
      }
    }

    // ── SQL Injection patterns ──
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (/(?:query|execute|raw)\s*\(\s*[`'"].*\$\{/.test(line) ||
          /(?:query|execute|raw)\s*\(\s*['"].*\+\s*(?:req|input|params|body)/.test(line)) {
        issues.push({
          id: nextId(),
          severity: 'error',
          title: 'Potential SQL Injection',
          description: 'String concatenation or template literals in SQL queries. Use parameterized queries.',
          file: name,
          line: i + 1,
        });
      }
    }

    // ── eval() usage ──
    for (let i = 0; i < lines.length; i++) {
      if (/\beval\s*\(/.test(lines[i]) && !lines[i].trim().startsWith('//')) {
        issues.push({
          id: nextId(),
          severity: 'warning',
          title: 'eval() Usage Detected',
          description: 'eval() executes arbitrary code and is a common attack vector. Avoid if possible.',
          file: name,
          line: i + 1,
        });
      }
    }

    // ── Unvalidated redirect ──
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (/(?:window\.location|location\.href)\s*=\s*(?:req|params|query|input|searchParams)/.test(line)) {
        issues.push({
          id: nextId(),
          severity: 'warning',
          title: 'Open Redirect Vulnerability',
          description: 'Redirecting to user-controlled URLs can enable phishing attacks. Validate the target URL.',
          file: name,
          line: i + 1,
        });
      }
    }

    // ── CORS wildcard ──
    for (let i = 0; i < lines.length; i++) {
      if (/Access-Control-Allow-Origin['":\s]*\*/.test(lines[i])) {
        issues.push({
          id: nextId(),
          severity: 'warning',
          title: 'CORS Wildcard (*) Allows Any Origin',
          description: 'Wildcard CORS allows any website to make requests. Restrict to specific origins if handling sensitive data.',
          file: name,
          line: i + 1,
        });
      }
    }

    // ── Missing authentication checks on API routes ──
    if (/(?:app\.(?:get|post|put|delete|patch)|router\.(?:get|post|put|delete|patch))/.test(content) &&
        !/(?:auth|session|token|middleware|isAuthenticated|requireAuth|getUser)/.test(content) &&
        (name.endsWith('.ts') || name.endsWith('.js'))) {
      issues.push({
        id: nextId(),
        severity: 'info',
        title: 'API Routes May Lack Authentication',
        description: 'No authentication middleware detected in API route definitions. Ensure endpoints are protected.',
        file: name,
      });
    }

    // ── localStorage for sensitive data ──
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (/localStorage\.setItem\s*\(\s*['"](?:token|jwt|session|password|secret|auth)/i.test(line)) {
        issues.push({
          id: nextId(),
          severity: 'warning',
          title: 'Sensitive Data Stored in localStorage',
          description: 'Storing tokens/secrets in localStorage is vulnerable to XSS. Consider httpOnly cookies.',
          file: name,
          line: i + 1,
        });
      }
    }

    // ── Insecure HTTP URLs ──
    for (let i = 0; i < lines.length; i++) {
      if (/['"]http:\/\/(?!localhost|127\.0\.0\.1|0\.0\.0\.0)/.test(lines[i]) &&
          !lines[i].trim().startsWith('//') && !lines[i].trim().startsWith('*')) {
        issues.push({
          id: nextId(),
          severity: 'info',
          title: 'Insecure HTTP URL',
          description: 'Using HTTP instead of HTTPS can expose data in transit. Upgrade to HTTPS.',
          file: name,
          line: i + 1,
        });
      }
    }

    // ── console.log with sensitive data ──
    for (let i = 0; i < lines.length; i++) {
      if (/console\.log\s*\(.*(?:password|secret|token|key|credential)/i.test(lines[i])) {
        issues.push({
          id: nextId(),
          severity: 'warning',
          title: 'Logging Sensitive Data',
          description: 'console.log may leak secrets in browser dev tools or server logs.',
          file: name,
          line: i + 1,
        });
      }
    }

    // ── document.cookie access ──
    for (let i = 0; i < lines.length; i++) {
      if (/document\.cookie/.test(lines[i]) && !lines[i].trim().startsWith('//')) {
        issues.push({
          id: nextId(),
          severity: 'info',
          title: 'Direct Cookie Access',
          description: 'Direct document.cookie manipulation. Consider using httpOnly cookies set by the server.',
          file: name,
          line: i + 1,
        });
      }
    }

    // ── Disabled security features ──
    for (let i = 0; i < lines.length; i++) {
      if (/(?:NODE_TLS_REJECT_UNAUTHORIZED|rejectUnauthorized)\s*[:=]\s*(?:0|false|'0'|"0")/.test(lines[i])) {
        issues.push({
          id: nextId(),
          severity: 'error',
          title: 'TLS Certificate Verification Disabled',
          description: 'Disabling TLS verification allows man-in-the-middle attacks.',
          file: name,
          line: i + 1,
        });
      }
    }
  }

  // Deduplicate by title+file (keep first occurrence)
  const seen = new Set<string>();
  return issues.filter(issue => {
    const key = `${issue.title}:${issue.file || ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// ── Component ───────────────────────────────────────────────────────────────

export function SecurityScanPanel({ files, onScanComplete }: SecurityScanPanelProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [ignoredIds, setIgnoredIds] = useState<Set<string>>(new Set());
  const [activeFilter, setActiveFilter] = useState<Severity | 'all' | 'ignored'>('all');

  const issues = useMemo(() => scanFiles(files), [files]);

  useEffect(() => {
    onScanComplete?.(issues);
  }, [issues]);

  const visibleIssues = useMemo(() => {
    if (activeFilter === 'ignored') return issues.filter(i => ignoredIds.has(i.id));
    return issues
      .filter(i => !ignoredIds.has(i.id))
      .filter(i => activeFilter === 'all' || i.severity === activeFilter);
  }, [issues, activeFilter, ignoredIds]);

  const counts = useMemo(() => ({
    error: issues.filter(i => i.severity === 'error' && !ignoredIds.has(i.id)).length,
    warning: issues.filter(i => i.severity === 'warning' && !ignoredIds.has(i.id)).length,
    info: issues.filter(i => i.severity === 'info' && !ignoredIds.has(i.id)).length,
    all: issues.filter(i => !ignoredIds.has(i.id)).length,
    ignored: ignoredIds.size,
  }), [issues, ignoredIds]);

  const toggleIgnore = (id: string) => {
    setIgnoredIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const SeverityIcon = ({ severity }: { severity: Severity }) => {
    if (severity === 'error') return <AlertCircle size={14} className={styles.iconError} />;
    if (severity === 'warning') return <AlertTriangle size={14} className={styles.iconWarning} />;
    return <Info size={14} className={styles.iconInfo} />;
  };

  if (issues.length === 0) {
    return (
      <div className={styles.panel}>
        <div className={styles.header}>
          <Shield size={16} className={styles.headerIcon} />
          <span className={styles.headerTitle}>Security Scan</span>
        </div>
        <div className={styles.clean}>
          <Shield size={32} className={styles.cleanIcon} />
          <p>No issues detected</p>
          <span className={styles.cleanSub}>Your code looks clean</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <Shield size={16} className={styles.headerIcon} />
        <span className={styles.headerTitle}>Detected Issues</span>
      </div>

      {/* Filter tabs */}
      <div className={styles.filterRow}>
        <button
          className={`${styles.filterTab} ${activeFilter === 'error' ? styles.filterTabActiveError : ''}`}
          onClick={() => setActiveFilter(activeFilter === 'error' ? 'all' : 'error')}
        >
          <span className={styles.filterCount} data-severity="error">{counts.error}</span> Errors
        </button>
        <button
          className={`${styles.filterTab} ${activeFilter === 'warning' ? styles.filterTabActiveWarning : ''}`}
          onClick={() => setActiveFilter(activeFilter === 'warning' ? 'all' : 'warning')}
        >
          <span className={styles.filterCount} data-severity="warning">{counts.warning}</span> Warnings
        </button>
        <button
          className={`${styles.filterTab} ${activeFilter === 'info' ? styles.filterTabActiveInfo : ''}`}
          onClick={() => setActiveFilter(activeFilter === 'info' ? 'all' : 'info')}
        >
          <span className={styles.filterCount} data-severity="info">{counts.info}</span> Info
        </button>
        <button
          className={`${styles.filterTab} ${activeFilter === 'all' ? styles.filterTabActiveAll : ''}`}
          onClick={() => setActiveFilter('all')}
        >
          All
        </button>
        {counts.ignored > 0 && (
          <button
            className={`${styles.filterTab} ${activeFilter === 'ignored' ? styles.filterTabActiveIgnored : ''}`}
            onClick={() => setActiveFilter(activeFilter === 'ignored' ? 'all' : 'ignored')}
          >
            <span className={styles.filterCount} data-severity="ignored">{counts.ignored}</span> Ignored
          </button>
        )}

        {counts.error > 0 && (
          <span className={styles.criticalBadge}>Critical problems that need your attention right away</span>
        )}
      </div>

      {/* Issues list */}
      <div className={styles.issuesList}>
        <div className={styles.issuesHeader}>
          <span className={styles.issuesHeaderCol}>Level</span>
          <span className={styles.issuesHeaderCol}>Issue</span>
        </div>
        {visibleIssues.map(issue => (
          <div key={issue.id} className={styles.issueRow}>
            <div
              className={styles.issueRowMain}
              onClick={() => setExpandedId(expandedId === issue.id ? null : issue.id)}
            >
              <ChevronRight
                size={12}
                className={`${styles.chevron} ${expandedId === issue.id ? styles.chevronOpen : ''}`}
              />
              <span className={`${styles.severityBadge} ${styles[`severity_${issue.severity}`]}`}>
                {issue.severity.charAt(0).toUpperCase() + issue.severity.slice(1)}
              </span>
              <span className={styles.issueTitle}>
                {issue.title}
                {issue.file && (
                  <span className={styles.issueFile}>{issue.file}{issue.line ? `:${issue.line}` : ''}</span>
                )}
              </span>
              <button
                className={styles.ignoreBtn}
                onClick={(e) => { e.stopPropagation(); toggleIgnore(issue.id); }}
                title={ignoredIds.has(issue.id) ? 'Restore' : 'Ignore'}
              >
                <EyeOff size={12} />
              </button>
            </div>
            {expandedId === issue.id && (
              <div className={styles.issueDetail}>
                <p>{issue.description}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
